import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PageHeader, Panel, StatTile, Avatar, PillTabs, height, fmt, positionsOf, shortDate } from "../../components/app/kit";
import { StatsPanel } from "../../components/app/StatsPanel";
import { CombinePanel } from "../../components/app/CombinePanel";
import { TradingCard } from "../../components/app/TradingCard";
import { BenchmarkRank } from "../../components/app/BenchmarkRank";
import { Court, CourtLines, ShotStats, ViewToggle } from "../../components/app/ShotChart";
import { HotZones } from "../../components/app/HotZones";
import { DEMO_TEAM, DEMO_GAMES, demoPlayer, shotSummary, combineFor } from "../../lib/demoData";

const DemoShots = ({ p }) => {
  const [view, setView] = useState("shots");
  const [gid, setGid] = useState("");
  const shots = p.shots.filter((s) => !gid || s.game_id === gid);
  const games = p.perGame.filter((g) => !gid || g.game_id === gid);
  const summary = shotSummary(shots, games.reduce((t, g) => t + g.ftm, 0), games.reduce((t, g) => t + g.fta, 0));
  return (
    <Panel data-testid="demo-shot-chart">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-display text-xl font-bold uppercase text-white">Shot chart</h3>
          <p className="text-sm text-slate-400">Coaches tap the court to log every make and miss. 3s use the high school line.</p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <ViewToggle value={view} onChange={setView} />
          <select data-testid="demo-shot-game-select" value={gid} onChange={(e) => setGid(e.target.value)} className="w-full rounded-full border border-[#1E293B] bg-[#162032] px-4 py-2.5 text-sm text-white sm:w-auto">
            <option value="">Season (all games)</option>
            {DEMO_GAMES.map((g) => <option key={g.id} value={g.id}>{`${shortDate(g.date)} \u00b7 vs ${g.opponent}`}</option>)}
          </select>
        </div>
      </div>
      <div className="space-y-5">
        <ShotStats s={summary} />
        {view === "zones" ? <HotZones shots={shots} lines={<CourtLines />} /> : <Court shots={shots} />}
      </div>
    </Panel>
  );
};

export default function DemoPlayer() {
  const { playerId } = useParams();
  const p = demoPlayer(playerId);
  const [tab, setTab] = useState("Stats");
  if (!p) return <Navigate to="/demo" replace />;
  const combine = combineFor(p.id);
  return (
    <div data-testid="demo-player-page">
      <Link to="/demo" data-testid="demo-back-link" className="mb-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" />{DEMO_TEAM.name} (sample)</Link>
      <PageHeader eyebrow={`Sample player report \u00b7 ${positionsOf(p)} \u00b7 #${p.jersey} \u00b7 Class of ${p.grad_year}`} title={p.name}>
        <Avatar name={p.name} size="h-16 w-16" />
      </PageHeader>
      <BenchmarkRank className="-mt-4 mb-6" player={p} bests={combine?.bests} />
      <div className="mb-6 flex items-center gap-4">
        <TradingCard p={p} teamName={`${DEMO_TEAM.name} (sample)`} season={p.season} perGame={p.perGame} />
        <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label="Height" value={height(p.height_in)} /><StatTile label="Weight" value={`${p.weight_lb} lb`} />
          <StatTile label="GPA" value={fmt(p.gpa)} /><StatTile label="Archetype" value={<span className="text-base sm:text-lg">{p.primary_archetype}</span>} />
        </div>
      </div>
      <PillTabs tabs={["Stats", "Shot Chart", "Combine"]} value={tab} onChange={setTab} />
      {tab === "Stats" && <StatsPanel season={p.season} perGame={p.perGame} />}
      {tab === "Shot Chart" && <DemoShots p={p} />}
      {tab === "Combine" && (combine
        ? <CombinePanel player={p} latest={combine.latest} bests={combine.bests} comparison={combine.comparison} sessions={combine.sessions} />
        : <CombinePanel emptyText={"This sample player has no combine testing. Open Jordan Ellis to see a combine comparison."} />)}
    </div>
  );
}
